import * as React from 'react'
import { PaymentStatus } from 'codegen/types'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { useUpgradeOrExtendSubscription } from 'payments/hooks/use-upgrade-or-extend-subscriptions'
import { localStore } from 'utils/storage'

import { PROMO_POPUPS_CONFIG } from './config'
import { useTranslations } from '../../contexts/header-config-context'
import type { UpgradeSource } from 'payments/upgrade-sources'

type UpgradeOrExtensionPopupTypes = 'LOGGED_IN_TRIAL' | 'LOGGED_IN_MONTHLY' | 'LOGGED_IN_YEARLY'

export const useHandleUpgradeOrExtension = (
  popupType: UpgradeOrExtensionPopupTypes,
  upgradeSource: UpgradeSource,
) => {
  const t = useTranslations()
  const [error, setError] = React.useState<string>('')
  const [externalUrl, setExternalUrl] = React.useState<React.ReactNode>('')
  const [success, setSuccess] = React.useState<string>('')
  const { open: openPayments } = usePaymentsDialog()
  const { charge, isProcessing } = useUpgradeOrExtendSubscription({ upgradeSource })

  // oxlint-disable-next-line complexity
  const handleUpgradeOrExtension = async (stripeCustomerId: string | null) => {
    setError('')
    setExternalUrl('')
    if (isProcessing) {
      return
    }

    savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.PROMO_POPUP)

    const response = await charge()

    if (response.userBanned) {
      setError(t('popups.user_banned'))
      return
    }
    if (response.maxExtensionsReached) {
      setError(t('popups.limit_reached'))
      return
    }
    if (response.paymentsStatus === PaymentStatus.RequiresAction) {
      const url = (stripeCustomerId && response.message) ?? ''
      const urlNode = (
        <>
          <br />
          {t('popups.click')}{' '}
          <a className="underline" target="_blank" href={url} rel="noreferrer">
            {t('popups.here')}
          </a>{' '}
          {t('popups.partial_to_complete')}
        </>
      )
      setExternalUrl(urlNode)
      setError(t('popups.additional_steps'))
      return
    }
    if (response.paymentsStatus === PaymentStatus.Processing) {
      setSuccess(t('popups.processing_update'))
      return
    }
    if (response.paymentsStatus === PaymentStatus.Failed) {
      if (stripeCustomerId && response.message) {
        const text = t('popups.failed_update').replace(
          '#type#',
          response.type === 'upgrade' ? t('popups.upgrade') : t('popups.extend'),
        )
        setError(text)
        const nodeUrl = (
          <>
            <br />
            {t('popups.click')}{' '}
            <a className="underline" target="_blank" href={response.message} rel="noreferrer">
              {t('popups.here')}
            </a>{' '}
            {t('popups.partial_to_complete')}
          </>
        )
        setExternalUrl(nodeUrl)
      } else {
        setError(t('popups.invalid_payment_method'))
        const nodeUrl = (
          <>
            {t('popups.click')}{' '}
            <a
              className="underline"
              target="_blank"
              href={AppRoutes.MY_ACCOUNT_PAYMENT_METHODS}
              rel="noreferrer"
            >
              {t('popups.here')}
            </a>{' '}
          </>
        )
        setExternalUrl(nodeUrl)
      }
      return
    }
    if (response.error?.type === 'noSubscriptionId') {
      if (typeof window !== 'undefined') {
        localStore.setString(PROMO_POPUPS_CONFIG[popupType].CLOSE_KEY, Date.now().toString())
      }
      if (window.cfOpenPaymentsDialog) {
        window?.cfOpenPaymentsDialog({
          promo: 'easter2025',
          defaultPlan: 'yearly',
          useCase: 'popup-promo',
        })
      } else {
        openPayments('yearly')
      }
    }
    if (response.error || response.paymentsStatus !== PaymentStatus.Succeeded) {
      const errorMessage =
        (response.error?.type === 'extendFailed' && t('popups.failed_extend_error')) ||
        (response.error?.type === 'upgradeFailed' && t('popups.failed_upgrade_error')) ||
        t('popups.payment_details_error').replace(
          '#type#',
          response.type === 'upgrade' ? t('popups.upgrade') : t('popups.extend'),
        )

      setError(errorMessage)
    }

    if (response.paymentsStatus === PaymentStatus.Succeeded) {
      setError('')
      setExternalUrl('')
      setSuccess('Subscription updated successfully!')
      if (typeof window !== 'undefined') {
        localStore.setString(PROMO_POPUPS_CONFIG[popupType].CLOSE_KEY, Date.now().toString())
      }
    }

    return
  }

  return {
    handleUpgradeOrExtension,
    error,
    externalUrl,
    success,
    isProcessing,
  }
}
